import { useCallback, useEffect, useState } from "react";
import { Title, useNotify } from "react-admin";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { AttendanceDayDrawer } from "@/containers/staff/staff-attendance/components/AttendanceDayDrawer";
import type {
  AttendanceDayCell,
  AttendanceEmployeeRow,
  AttendanceMonthResponse,
  DayDrilldown,
} from "@/containers/staff/staff-attendance/attendance-types";
import { StaffLeaveHistorySection } from "@/containers/staff/staff/components/StaffLeaveHistorySection";
import { StaffLeaveBalanceCard } from "@/containers/staff/staff/components/StaffLeaveBalanceCard";
import { resolveStaffDashboardMonth } from "@/containers/staff/staff/staff-show-helpers";
import { StaffMonthlyAttendancePanel } from "@/containers/staff/shared/StaffMonthlyAttendancePanel";
import { callApi } from "@/dataprovider/misc-apis";

/**
 * Personal hub: own month attendance + own leave (myAttendance.view).
 * Does not expose department grid, salary notes, or other employees.
 */
export default function MyAttendancePage() {
  const notify = useNotify();
  const [month, setMonth] = useState(() => resolveStaffDashboardMonth(null));
  const [loading, setLoading] = useState(false);
  const [employeeRow, setEmployeeRow] = useState<AttendanceEmployeeRow | null>(null);
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [offWeekdays, setOffWeekdays] = useState<number[]>([0]);
  const [lateCheckInLocal, setLateCheckInLocal] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<DayDrilldown | null>(null);

  const fetchAttendance = useCallback(async () => {
    setLoading(true);
    setLinkError(null);
    try {
      const { data: response } = await callApi({
        location: "employeesAttendance/me",
        data: { month },
        method: "GET",
      });
      const body = response as AttendanceMonthResponse;
      const row = Array.isArray(body?.data) ? (body.data[0] ?? null) : null;
      setEmployeeRow(row);
      setTimezone(body?.timezone || "Asia/Kolkata");
      setOffWeekdays(Array.isArray(body?.offWeekdays) ? body.offWeekdays : [0]);
      setLateCheckInLocal(body?.lateCheckInLocal ?? null);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setLinkError(
          "Your login is not linked to a staff record. Ask an office admin to enable JMS login on your employee profile."
        );
        setEmployeeRow(null);
      } else {
        notify("Failed to load your attendance", { type: "error" });
        setEmployeeRow(null);
      }
    } finally {
      setLoading(false);
    }
  }, [month, notify]);

  useEffect(() => {
    void fetchAttendance();
  }, [fetchAttendance]);

  const handleDayClick = (day: number, cell: AttendanceDayCell) => {
    if (!employeeRow) return;
    setDrilldown({ employee: employeeRow, day, cell });
  };

  return (
    <Box sx={{ maxWidth: 960, px: { xs: 1, sm: 2 }, pb: 3 }}>
      <Title title="My dashboard" />
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 0.5 }}>
            My dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {employeeRow?.name ? employeeRow.name : "Your monthly attendance"}
            {employeeRow?.employeeId != null ? ` · Id ${employeeRow.employeeId}` : ""}
          </Typography>
        </Box>

        {linkError ? <Alert severity="warning">{linkError}</Alert> : null}

        {!linkError ? (
          <StaffMonthlyAttendancePanel
            month={month}
            onMonthChange={setMonth}
            loading={loading}
            employeeRow={employeeRow}
            timezone={timezone}
            offWeekdays={offWeekdays}
            lateCheckInLocal={lateCheckInLocal}
            onRefresh={() => void fetchAttendance()}
            onDayClick={handleDayClick}
            toolbarActions={
              <Button component={RouterLink} to="/leaveCalendar" variant="outlined" size="small">
                View team leave calendar
              </Button>
            }
          />
        ) : null}

        {!linkError && employeeRow?.employeeId != null ? (
          <>
            <StaffLeaveBalanceCard mode="self" employeeId={employeeRow.employeeId} />
            <StaffLeaveHistorySection mode="self" employeeId={employeeRow.employeeId} />
          </>
        ) : null}
      </Stack>

      <AttendanceDayDrawer
        open={drilldown != null}
        drilldown={drilldown}
        month={month}
        timezone={timezone}
        onClose={() => setDrilldown(null)}
        showStaffDashboardLink={false}
      />
    </Box>
  );
}
